<template>
  <div>
    <Dialog v-model:visible="visible" modal :closable="false" header="Select Logo" :style="{ width: '50vw' }" :breakpoints="{ '1199px': '75vw', '575px': '90vw' }">
      <template #header>
        <div class="w-full flex items-center justify-between">
          <div class="font-semibold text-xl white-space-nowrap">Select Logo</div>
          <div class="flex rounded-md shadow-sm">
            <div class="relative flex-grow focus-within:z-10">
              <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <MagnifyingGlassIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                type="text"
                v-model="logoNameFilterInput"
                class="w-48 rounded-none rounded-l-md border-0 py-1.5 pl-10 text-sm leading-6 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600"
                placeholder="Filter"
              />
            </div>
            <button type="button" class="relative -ml-px inline-flex items-center gap-x-1.5 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50" @click="filterLogos">
              <MagnifyingGlassIcon class="-ml-0.5 h-5 w-5 text-gray-400" aria-hidden="true" />
            </button>
            <button
              type="button"
              class="relative -ml-px inline-flex items-center gap-x-1.5 rounded-r-md px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              @click="
                () => {
                  logoNameFilterInput = '';
                  filterLogos();
                }
              "
            >
              <XMarkIcon class="-ml-0.5 h-5 w-5 text-gray-400" aria-hidden="true" />
            </button>
          </div>
        </div>
      </template>
      <div class="">
        <Carousel ref="logoCarousel" :value="filteredLogos" :numVisible="3" :numScroll="3" :responsiveOptions="responsiveOptions" class="">
          <template #item="slotProps">
            <div :class="['border rounded-lg m-2 p-3 cursor-pointer', getLogoClass(slotProps.data)]" @click="onSelectLogo(slotProps.data)">
              <div class="mb-3 text-xs">{{ slotProps.data.name }}</div>
              <div class="mb-3">
                <div class="relative mx-auto">
                  <img :src="slotProps.data.url" :alt="slotProps.data.name" class="max-h-32" />
                </div>
              </div>
            </div>
          </template>
        </Carousel>
      </div>
      <Divider align="left" type="solid"> Add new logo </Divider>
      <div class="flex items-center">
        <div class="min-h-[8rem]">
          <img v-if="uploadedLogo" :src="uploadedLogo" alt="Logo" class="w-[8rem]" />
          <img v-else-if="newLogoUrl && newLogoUrl.length > 0" :src="newLogoUrl" alt="Logo" class="w-[8rem]" />
          <Skeleton v-else animation="none" size="8rem" class=""></Skeleton>
        </div>
        <div class="ml-4 flex-grow">
          <div class="mt-2 flex items-center justify-between">
            <div
              :class="['border border-dashed p-2 rounded-md', dragOver ? 'bg-slate-100 border-slate-500' : 'bg-slate-50 border-slate-300']"
              @dragover.prevent="dragOver = true"
              @dragleave.prevent="dragOver = false"
              @drop.prevent="handleDrop"
            >
              <div>
                <p class="upload-text">Drag & Drop or <a href="#" @click.prevent="triggerFileInput" class="text-blue-600 underline">Upload Image</a></p>
              </div>
              <input ref="fileInput" type="file" accept="image/*" multiple class="hidden" @change="uploadChatLogo" />
            </div>
            <InputText id="doctitle" class="" v-model="newLogoUrl" aria-describedby="doctitle-help" placeholder="Or provide logo URL" />
          </div>
          <div class="mt-4 flex items-center justify-end gap-1">
            <InputText id="docname" class="w-full" v-model="newLogoName" aria-describedby="docname-help" placeholder="New logo name" />
            <Button label="Add" :disabled="!uploadedLogo && (!newLogoUrl || newLogoUrl.length <= 0)" severity="primary" @click="addLogo" autofocus />
          </div>
        </div>
      </div>
      <div class="mt-4">
        <small class="min-h-6 block">{{ progressText }} </small>
        <ProgressBar :mode="progressMode" style="height: 6px"></ProgressBar>
      </div>
      <template #footer>
        <Button label="Cancel" :disabled="showProgress" severity="secondary" @click="visible = false" autofocus />
        <Button label="Select" :disabled="showProgress || !selectedLogoUrl" severity="primary" @click="selectLogo" autofocus />
      </template>
    </Dialog>
  </div>
</template>

<script setup>
  import { useAuthStore } from '@/store/AuthStore';
  import { useApi } from '@/hooks/useApi';
  import { useToast } from 'vue-toastification';
  import { ref, nextTick, computed, watch } from 'vue';
  import InputText from 'primevue/inputtext';
  import Dialog from 'primevue/dialog';
  import Button from 'primevue/button';
  import ProgressBar from 'primevue/progressbar';
  import Carousel from 'primevue/carousel';
  import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/vue/20/solid';
  import { useCollection } from 'vuefire';
  import { doc, setDoc, getDoc, collection, deleteDoc } from 'firebase/firestore';
  import { fbDb } from '@/firebaseApp';
  import { getUUID } from '@enegelai/core';
  import Divider from 'primevue/divider';
  import Skeleton from 'primevue/skeleton';

  const authStore = useAuthStore();
  const api = useApi();
  const toast = useToast();

  const tenantId = computed(() => authStore.tenantId);

  // Collection of all logos for the tenant
  const logos = useCollection(collection(fbDb, `tenants/${tenantId.value}/logos`));

  const logoCarousel = ref(null);
  const fileInput = ref(null);

  const visible = ref(false);
  const showProgress = ref(false);
  const progressMode = computed(() => (showProgress.value ? 'indeterminate' : ''));
  const progressText = ref('');

  const responsiveOptions = ref([
    {
      breakpoint: '575px',
      numVisible: 1,
      numScroll: 1,
    },
  ]);

  const filteredLogos = computed(() => {
    const l = logoNameFilter.value && logoNameFilter.value.length > 0 ? logos.value.filter((logo) => logo.name.toLowerCase().includes(logoNameFilter.value.toLowerCase())) || [] : logos.value;
    const ls = l.sort((a, b) => b.updated - a.updated);
    return ls;
  });

  const selectedLogoUrl = ref(null);
  const logoNameFilter = ref('');
  const logoNameFilterInput = ref('');
  const newLogoName = ref('');
  const newLogoUrl = ref('');
  const chatLogos = ref([]);
  const uploadedLogo = ref(null);
  const dragOver = ref(false);
  const selectedLogoIndex = ref(0);

  function filterLogos() {
    logoNameFilter.value = logoNameFilterInput.value;
    logoCarousel.value.navBackward({}, 0);
  }

  const uploadChatLogo = (event) => {
    const files = event.target.files;
    if (!files) return;
    Array.from(files).forEach((file) => processFile(file));
    event.target.value = '';
  };

  const handleDrop = (event) => {
    dragOver.value = false;
    const files = event.dataTransfer.files;
    if (files && files.length) {
      Array.from(files).forEach((file) => processFile(file));
    }
  };

  const processFile = (file) => {
    const reader = new FileReader();
    newLogoName.value = file.name;
    reader.onload = (e) => {
      uploadedLogo.value = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const triggerFileInput = () => {
    fileInput.value.click();
  };

  const emit = defineEmits(['logoSelected']);

  function start() {
    visible.value = true;
  }

  const pause = (x) => new Promise((res) => setTimeout(res, x));

  function onSelectLogo(logoData) {
    selectedLogoUrl.value = logoData.url;
  }

  function isLogoSelected(logoUrl) {
    return selectedLogoUrl.value === logoUrl;
  }

  function getLogoClass(logoData) {
    return isLogoSelected(logoData.url) ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:bg-blue-50';
  }

  async function createLogoDocument(logoData) {
    const logoId = logoData?.id || getUUID();
    try {
      await setDoc(
        doc(fbDb, `tenants/${tenantId.value}/logos`, logoId),
        Object.assign({}, logoData, {
          updated: Date.now(),
          updatedBy: authStore.displayName,
        }),
        { merge: true },
      );
    } catch (e) {
      console.log(`Error writing document: ${e.message}`);
    }
  }

  function extractDataUrlComponents(dataUrl) {
    // This regex matches:
    //  - The content type (any characters up to the first semicolon)
    //  - The base64 encoded data (everything after "base64,")
    const regex = /^data:([^;]+);base64,(.*)$/;
    const matches = dataUrl.match(regex);

    if (!matches) {
      return {
        contentType: null,
        base64Data: null,
      };
    }

    return {
      contentType: matches[1],
      base64Data: matches[2],
    };
  }

  async function addLogo() {
    progressText.value = 'Adding logo ...';
    showProgress.value = true;

    const logoDocumentData = {
      id: getUUID(),
      name: newLogoName.value || 'New logo',
      url: newLogoUrl,
    };

    if (uploadedLogo.value) {
      // Need to upload logo to CDN
      const { contentType, base64Data } = extractDataUrlComponents(uploadedLogo.value);
      if (!contentType || !base64Data) {
        progressText.value = '';
        showProgress.value = false;
        toast.error(`Wrong image data`);
        return;
      }
      const ctParts = contentType.split('/');
      const ext = '.' + ctParts.length > 1 ? ctParts[1] : 'png';
      let fName = `${logoDocumentData.id}_${logoDocumentData.name}`;
      if (!fName.endsWith(ext)) {
        fName += ext;
      }
      const cdnUrl = await api.publishToCDN({
        contentType,
        data: base64Data,
        path: `logos/${fName}`,
      });
      // todo normalize url
      logoDocumentData.url = cdnUrl;
    }

    if (!logoDocumentData.url || logoDocumentData.url.length <= 0) {
      progressText.value = '';
      showProgress.value = false;
      toast.error(`No image provided`);
      return;
    }

    await createLogoDocument(logoDocumentData);

    newLogoName.value = '';
    newLogoUrl.value = '';
    uploadedLogo.value = null;

    progressText.value = '';
    showProgress.value = false;

    //await nextTick();
    //emit('logoAdded', createRes.data);
  }

  async function selectLogo() {
    visible.value = false;
    await nextTick();
    emit('logoSelected', selectedLogoUrl.value);
  }

  defineExpose({ start });
</script>
